import { useState } from 'react';
import { Avatar, Badge, ConsolePage, DataTable, Icon, Panel, QR, QueryState, ListSkeleton, Td, useToast, type Column } from '@/components';
import { useCourses, useRoster, useSetCheckin, useMarkAttended } from '@/hooks/queries';
import { initialsOf } from '@/lib/format';
import type { RosterEntry } from '@/types/domain';

function CertChip({ state }: { state: RosterEntry['cert'] }) {
  if (state === 'issued') return <Badge kind="sage" dot>Issued</Badge>;
  if (state === 'ready') return <Badge kind="ochre" dot>Ready</Badge>;
  return <Badge kind="gray">Blocked</Badge>;
}

const COLS: Column[] = [
  { label: 'Learner' },
  { label: 'Unit' },
  { label: 'Lessons', align: 'center' },
  { label: 'Attend', align: 'center' },
  { label: 'Pre', align: 'center' },
  { label: 'Post', align: 'center' },
  { label: 'Eval', align: 'center' },
  { label: 'Certificate' },
];

export function InstructorLearners() {
  const { toast } = useToast();
  const { data: courses = [] } = useCourses();
  const courseId = courses[0]?.id;
  const rosterQuery = useRoster(courseId);
  const roster = rosterQuery.data ?? [];
  const setCheckin = useSetCheckin();
  const markAttended = useMarkAttended();
  // The opened window state + venue token live here for the session (re-open is idempotent).
  const [checkin, setCheckinState] = useState<{ open: boolean; token: string } | null>(null);
  const [deptFilter, setDeptFilter] = useState('all');
  const [unitFilter, setUnitFilter] = useState('all');
  const [search, setSearch] = useState('');
  const deptOptions = [...new Set(roster.map((r) => r.dept).filter(Boolean))].sort();
  const unitOptions = [...new Set(roster.filter((r) => deptFilter === 'all' || r.dept === deptFilter).map((r) => r.unit).filter(Boolean))].sort();
  const q = search.trim().toLowerCase();
  const filtered = roster.filter(
    (r) =>
      (deptFilter === 'all' || r.dept === deptFilter) &&
      (unitFilter === 'all' || r.unit === unitFilter) &&
      (q === '' || `${r.name} ${r.th} ${r.unit} ${r.emp}`.toLowerCase().includes(q)),
  );

  const appUrl = import.meta.env.VITE_APP_URL ?? 'https://warmpaper.club';
  const venueUrl = checkin?.open && courseId ? `${appUrl}/?checkin=${checkin.token}&course=${courseId}` : '';

  const toggleCheckin = (open: boolean) => {
    if (!courseId) return;
    setCheckin.mutate(
      { courseId, open },
      {
        onSuccess: (res) => { setCheckinState(res); toast(open ? 'Check-in is open' : 'Check-in closed', { icon: open ? 'qr' : 'lock', tone: open ? 'sage' : 'clay' }); },
        onError: () => toast('Could not update check-in', { tone: 'clay', icon: 'x' }),
      },
    );
  };

  const mark = (r: RosterEntry) => {
    if (!courseId || !r.userId || r.attended) return;
    markAttended.mutate(
      { courseId, userId: r.userId },
      {
        onSuccess: () => toast(`${r.name} marked present`, { tone: 'sage', icon: 'check' }),
        onError: (err) => {
          const msg = (err as { message?: string } | null)?.message ?? String(err);
          toast(msg.includes('lessons_incomplete') ? `${r.name} hasn’t finished the lessons` : 'Could not mark attendance', { tone: 'clay', icon: 'x' });
        },
      },
    );
  };

  return (
    <ConsolePage title="Learner management" th="จัดการผู้เรียน" crumbs="SESSIONS" back="/teach">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Panel title="Attendance check-in · เช็คอินหน้างาน">
          {checkin?.open && venueUrl ? (
            <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ padding: 10, background: '#fff', borderRadius: 12, border: '1px solid var(--line)', flex: 'none' }}>
                <QR value={venueUrl} size={140} />
              </div>
              <div style={{ flex: 1, minWidth: 220 }}>
                <Badge kind="sage" dot>Open</Badge>
                <p style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.55, margin: '10px 0 14px' }}>
                  Display this QR at the venue — learners scan it to check in. If someone’s scan fails, tap <strong>Mark</strong> on their row below.
                </p>
                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} disabled={setCheckin.isPending} onClick={() => toggleCheckin(false)}>
                  <Icon name="lock" size={14} /> Close check-in
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <div style={{ fontSize: 13.5, color: 'var(--ink-3)', maxWidth: 380, lineHeight: 1.5 }}>
                Open check-in to show the venue QR and let learners scan themselves present. You control when it’s open.
              </div>
              <button className="btn btn-primary btn-sm" disabled={setCheckin.isPending || !courseId} onClick={() => toggleCheckin(true)}>
                <Icon name="qr" size={15} /> Open check-in
              </button>
            </div>
          )}
        </Panel>

        {roster.length > 0 && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <label className="field" style={{ flex: '2 1 220px', minWidth: 0, marginBottom: 0 }}>
              <span className="label">Search</span>
              <input className="input" placeholder="Search by name…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </label>
            <label className="field" style={{ flex: '1 1 160px', minWidth: 0, marginBottom: 0 }}>
              <span className="label">Department</span>
              <select className="select" value={deptFilter} onChange={(e) => { setDeptFilter(e.target.value); setUnitFilter('all'); }}>
                <option value="all">All departments</option>
                {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
            <label className="field" style={{ flex: '1 1 160px', minWidth: 0, marginBottom: 0 }}>
              <span className="label">Unit</span>
              <select className="select" value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)}>
                <option value="all">All units</option>
                {unitOptions.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </label>
          </div>
        )}
        <QueryState query={rosterQuery} skeleton={<ListSkeleton rows={6} />} isEmpty={() => roster.length === 0} emptyIcon="users" emptyTitle="No learners enrolled yet">
          {() => (
            <Panel title={`Learners · ${filtered.length}`} pad={false}>
              <DataTable
                cols={COLS}
                rows={filtered}
                renderRow={(r) => (
                  <>
                    <Td>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <Avatar initials={initialsOf(r.name.split(' ')[0], r.name.split(' ')[1] ?? '')} size={32} tone={r.tone ?? 'slate'} photoUrl={r.photoUrl} />
                        <div>
                          <div style={{ fontWeight: 500 }}>{r.name}</div>
                          <div className="th" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{r.emp}</div>
                        </div>
                      </div>
                    </Td>
                    <Td><span className="muted2" style={{ fontSize: 13 }}>{r.unit}</span></Td>
                    <Td align="center"><span className="u-mono" style={{ color: r.lessons === 100 ? 'var(--sage-deep)' : 'var(--ink-3)' }}>{r.lessons}%</span></Td>
                    <Td align="center">
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {r.attended ? (
                          <Icon name="check" size={16} style={{ color: 'var(--sage)' }} />
                        ) : r.userId && r.lessons < 100 ? (
                          <span style={{ fontSize: 11.5, color: 'var(--ink-4)', display: 'flex', alignItems: 'center', gap: 3 }} title="Lessons not finished"><Icon name="lock" size={12} /> {r.lessons}%</span>
                        ) : r.userId ? (
                          <button className="btn btn-ghost btn-sm" style={{ padding: '3px 10px', fontSize: 12 }} disabled={markAttended.isPending} onClick={() => mark(r)}>Mark</button>
                        ) : (
                          <Icon name="x" size={15} style={{ color: 'var(--clay)' }} />
                        )}
                      </div>
                    </Td>
                    <Td align="center"><span className="u-mono muted" style={{ fontSize: 13 }}>{r.pretest ?? '—'}</span></Td>
                    <Td align="center">{r.posttest != null ? <span className="u-mono" style={{ fontWeight: 600, color: r.posttest >= 80 ? 'var(--sage-deep)' : 'var(--clay)' }}>{r.posttest}</span> : <span className="muted">—</span>}</Td>
                    <Td align="center"><div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{r.eval ? <Icon name="check" size={16} style={{ color: 'var(--sage)' }} /> : <span className="muted">—</span>}</div></Td>
                    <Td><CertChip state={r.cert} /></Td>
                  </>
                )}
              />
            </Panel>
          )}
        </QueryState>
      </div>
    </ConsolePage>
  );
}
